"use client"

import { FormikProvider, useFormik, useFormikContext } from "formik"
import { BookOpen, ChevronDown, Globe, Loader2, SlidersHorizontal, Sparkles } from "lucide-react"

import { Button } from "@/components/ui/button"
import { OnboardingStepActions } from "@/components/onboarding/onboarding-step-actions"
import { Card, CardContent } from "@/components/ui/card"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { WebsiteUrlFormInput } from "@/components/ai/knowledge-base/website-url-form-input"
import { useImportKnowledgeBaseFromUrlMutation } from "@/hooks/mutations/knowledge-base-url-import"
import type { ImportKnowledgeBaseFromUrlResult } from "@/server-actions/knowledge-base-url-import"
import { resolveKbUrlImportDefaultMaxPages } from "@/lib/ai/knowledge-base/url-import/constants"
import {
  isValidWebsiteUrl,
  normalizeWebsiteUrlInput,
} from "@/lib/ai/knowledge-base/url-import/url"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




type FormValues = {
  url: string
  maxPages: number | ""
  maxDepth: number | ""
}

type WebsiteImportFormProps = {
  isDefault?: boolean
  maxPagesLimit: number
  balanceDepleted?: boolean
  showSkipAction?: boolean
  onImported?: (result: ImportKnowledgeBaseFromUrlResult, normalizedUrl: string) => void | Promise<void>
  onSkip: () => void
  onViewProgress: () => void
}

function validate(values: FormValues, maxPagesLimit: number) {
  const errors: Partial<Record<keyof FormValues, string>> = {}

  if (!values.url.trim()) {
    errors.url = "Website URL is required."
  } else if (!isValidWebsiteUrl(values.url)) {
    errors.url = "Enter a valid website URL."
  }

  if (
    typeof values.maxPages !== "number" ||
    !Number.isInteger(values.maxPages) ||
    values.maxPages < 1 ||
    values.maxPages > maxPagesLimit
  ) {
    errors.maxPages = `Use a whole number from 1 to ${maxPagesLimit}.`
  }

  if (
    typeof values.maxDepth !== "number" ||
    !Number.isInteger(values.maxDepth) ||
    values.maxDepth < 0 ||
    values.maxDepth > 5
  ) {
    errors.maxDepth = "Use a whole number from 0 to 5."
  }

  return errors
}

export function parseOnboardingNumberInput(value: string): number | "" {
  return value === "" ? "" : Number(value)
}

function NumberField({
  name,
  label,
  min,
  max,
  helper,
}: {
  name: "maxPages" | "maxDepth"
  label: string
  min: number
  max: number
  helper: string
}) {
  const { values, errors, touched, setFieldTouched, setFieldValue } = useFormikContext<FormValues>()
  const error = touched[name] && errors[name] ? errors[name] : null

  return (
    <div className="space-y-1.5">
      <Label htmlFor={`onboarding-kb-${name}`}>{label}</Label>
      <Input
        id={`onboarding-kb-${name}`}
        type="number"
        min={min}
        max={max}
        value={values[name]}
        aria-invalid={Boolean(error)}
        onBlur={() => {
          void setFieldTouched(name, true, true)
        }}
        onChange={(event) => {
          void setFieldValue(
            name,
            parseOnboardingNumberInput(event.target.value),
          )
        }}
      />
      <p className={error ? "text-xs font-medium text-destructive" : "text-xs text-muted-foreground"}>
        {error || helper}
      </p>
    </div>
  )
}

export function WebsiteImportForm({
  isDefault = true,
  maxPagesLimit,
  balanceDepleted = false,
  showSkipAction = true,
  onImported,
  onSkip,
  onViewProgress,
}: WebsiteImportFormProps) {

  const tUi = useTranslations("Onboarding.WebsiteImportForm")

  const importMutation = useImportKnowledgeBaseFromUrlMutation()

  const formik = useFormik<FormValues>({
    initialValues: {
      url: "",
      maxPages: resolveKbUrlImportDefaultMaxPages(maxPagesLimit),
      maxDepth: 3,
    },
    enableReinitialize: true,
    validate: (values) => validate(values, maxPagesLimit),
    onSubmit: async (values, helpers) => {
      try {
        if (
          typeof values.maxPages !== "number" ||
          typeof values.maxDepth !== "number"
        ) {
          return
        }

        const normalizedUrl = normalizeWebsiteUrlInput(values.url)
        const result = await importMutation.mutateAsync({
          url: normalizedUrl,
          maxPages: values.maxPages,
          maxDepth: values.maxDepth,
          category: undefined,
          tags: [],
          injectionMode: "auto",
          isDefault,
          setErrors: helpers.setErrors as (errors: Record<string, unknown>) => void,
        })
        await onImported?.(result, normalizedUrl)
      } catch {
        // withQueryHandling already maps validation errors and shows the toast.
      }
    },
  })

  const isSubmitting = importMutation.isPending
  const isAccepted = importMutation.isSuccess

  return (
    <Card className="overflow-hidden border-blue-200 bg-gradient-to-br from-blue-50 via-white to-violet-50">
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <CardContent className="p-0">
            <div className="grid gap-6 p-4 sm:p-6 lg:grid-cols-[minmax(0,1fr)_minmax(360px,440px)] lg:items-start">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-3 py-1 text-xs font-medium text-blue-700">
                  <Sparkles className="h-3.5 w-3.5" />
                  <span key={tUi("websiteSetup")}>{tUi("websiteSetup")}</span></div>
                <div className="space-y-1">
                  <h2 className="text-xl font-semibold tracking-tight"><span key={tUi("learnFromYourWebsite")}>{tUi("learnFromYourWebsite")}</span></h2>
                  <p className="max-w-2xl text-sm text-muted-foreground">
                    <span key={tUi("weWillCrawlTheSiteAndMakeYour")}>{tUi("weWillCrawlTheSiteAndMakeYour")}</span></p>
                  {balanceDepleted && (
                    <p className="text-sm font-medium text-amber-700">
                      <span key={tUi("topUpYourBalanceAfterThisStepSo")}>{tUi("topUpYourBalanceAfterThisStepSo")}</span></p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1">
                    <Globe className="h-3.5 w-3.5" />
                    <span key={tUi("recursiveCrawl")}>{tUi("recursiveCrawl")}</span></span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1"><TranslationText>{tUi.rich("tag0Tag0UpToValue0PageValue1", { value0: String(maxPagesLimit), value1: String(maxPagesLimit === 1 ? "" : "s"), tag0: () => (<BookOpen className="h-3.5 w-3.5" />) })}</TranslationText></span>
                </div>
              </div>

              <div className="rounded-lg border bg-white p-4 shadow-sm">
                {isAccepted ? (
                  <div className="space-y-3">
                    <div className="text-sm font-medium"><span key={tUi("importStarted")}>{tUi("importStarted")}</span></div>
                    <p className="text-sm text-muted-foreground">
                      <span key={tUi("theWebsiteIsBeingCrawledInTheBackground")}>{tUi("theWebsiteIsBeingCrawledInTheBackground")}</span></p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <WebsiteUrlFormInput placeholder={"example.com"} />

                    <Collapsible>
                      <CollapsibleTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          className="group w-full justify-between px-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
                        >
                          <span className="inline-flex items-center gap-2"><TranslationText>{tUi.rich("tag0Tag0AdvancedSettings", { tag0: () => (<SlidersHorizontal className="h-4 w-4" />) })}</TranslationText></span>
                          <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pt-2">
                        <div className="grid gap-3 rounded-md border bg-muted/30 p-3 sm:grid-cols-2">
                          <NumberField
                            name="maxPages"
                            label={tUi("maxPages")}
                            min={1}
                            max={maxPagesLimit}
                            helper={`Plan limit: ${maxPagesLimit}`}
                          />
                          <NumberField
                            name="maxDepth"
                            label={tUi("maxDepth")}
                            min={0}
                            max={5}
                            helper="0 imports only the entered page"
                          />
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                )}
              </div>
            </div>

            <OnboardingStepActions className="border-blue-100 bg-white/80 px-4 py-4 sm:px-6">
              {isAccepted ? (
                <>
                  <Button type="button" variant="outline" onClick={onSkip}>
                    <span key={tUi("continueToBilling")}>{tUi("continueToBilling")}</span></Button>
                  <Button type="button" onClick={onViewProgress}>
                    <span key={tUi("viewProgress")}>{tUi("viewProgress")}</span></Button>
                </>
              ) : (
                <>
                  {showSkipAction ? (
                    <Button type="button" variant="outline" onClick={onSkip} disabled={isSubmitting}>
                      <span key={tUi("skipForNow")}>{tUi("skipForNow")}</span></Button>
                  ) : null}
                  <Button type="submit" disabled={isSubmitting || !formik.values.url.trim()}>
                    {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {/* Keep the spinner's sibling stable when browser translation replaces text nodes. */}
                    <span><span key={tUi("startImport")}>{tUi("startImport")}</span></span>
                  </Button>
                </>
              )}
            </OnboardingStepActions>
          </CardContent>
        </form>
      </FormikProvider>
    </Card>
  )
}
